import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Check, CreditCard, Lock, Loader2 } from "lucide-react";
import { AddCardForm } from "./AddCardForm";

interface PurchaseCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId?: string;
  onPurchaseComplete?: () => void;
  onSuccess?: () => void;
}

const CREDIT_PACKAGES = [
  { credits: 1000, price: 10, popular: false },
  { credits: 2500, price: 25, popular: false },
  { credits: 5000, price: 50, popular: true },
  { credits: 10000, price: 100, popular: false },
  { credits: 20000, price: 200, popular: false },
];

export const PurchaseCreditsModal = ({
  open,
  onOpenChange,
  userId: propUserId,
  onPurchaseComplete,
  onSuccess,
}: PurchaseCreditsModalProps) => {
  const { toast } = useToast();
  const [selectedPackage, setSelectedPackage] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [userId, setUserId] = useState<string | null>(propUserId || null);

  // Get user ID if not provided
  useEffect(() => {
    if (!propUserId) {
      supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          setUserId(data.user.id);
        }
      });
    }
  }, [propUserId]);

  const handlePurchase = async () => {
    if (!userId) {
      toast({ title: "Error", description: "User not authenticated", variant: "destructive" });
      return;
    }
    setIsProcessing(true);
    
    try {
      const pkg = CREDIT_PACKAGES[selectedPackage];
      
      // For now, just add the credits directly (payment integration to be added later)
      // In production, this would redirect to Stripe or process payment first
      
      // Get current balance
      const { data: currentCredits } = await supabase
        .from("user_credits")
        .select("balance")
        .eq("user_id", userId)
        .maybeSingle();

      const currentBalance = currentCredits?.balance || 0;
      const newBalance = currentBalance + pkg.credits;

      // Update or insert credits
      if (currentCredits) {
        await supabase
          .from("user_credits")
          .update({ balance: newBalance })
          .eq("user_id", userId);
      } else {
        await supabase
          .from("user_credits")
          .insert({ user_id: userId, balance: pkg.credits });
      }

      // Log transaction
      await supabase.from("credit_transactions").insert({
        user_id: userId,
        amount: pkg.credits,
        type: "purchase",
        description: `Purchased ${pkg.credits.toLocaleString()} credits for $${pkg.price}`,
      });

      toast({
        title: "Credits purchased!",
        description: `${pkg.credits.toLocaleString()} credits have been added to your account`,
      });

      onPurchaseComplete?.();
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Purchase failed",
        description: error.message || "Failed to purchase credits",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddCard = (cardData: any) => {
    // For now, just show a toast - payment integration to be added later
    toast({
      title: "Card added",
      description: "Payment method saved. Payment integration coming soon.",
    });
    setShowAddCard(false);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      onOpenChange(value);
      if (!value) setShowAddCard(false);
    }}>
      <DialogContent className={showAddCard ? "sm:max-w-lg" : "sm:max-w-md"}>
        {showAddCard ? (
          <AddCardForm 
            onCancel={() => setShowAddCard(false)} 
            onAddCard={handleAddCard}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Purchase Credits</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 py-4">
              {CREDIT_PACKAGES.map((pkg, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedPackage(index)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    selectedPackage === index
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  } ${pkg.popular ? "ring-2 ring-amber-400 ring-offset-2" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center ${
                        selectedPackage === index
                          ? "bg-primary text-primary-foreground"
                          : "border border-border"
                      }`}
                    >
                      {selectedPackage === index && <Check className="w-3.5 h-3.5" />}
                    </div>
                    <span className="font-medium">
                      {pkg.credits.toLocaleString()} CREDITS
                    </span>
                    {pkg.popular && (
                      <span className="text-xs bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full font-medium">
                        POPULAR
                      </span>
                    )}
                  </div>
                  <span className="font-semibold">${pkg.price}</span>
                </button>
              ))}
            </div>

            <Button
              className="w-full"
              size="lg"
              disabled={true}
            >
              Payment Integration Coming Soon
            </Button>
            
            <p className="text-sm text-muted-foreground text-center">
              Credit purchases are temporarily unavailable. Payment processor integration is being configured.
            </p>

            <div className="pt-4 border-t border-border space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Select Card</span>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-primary"
                  onClick={() => setShowAddCard(true)}
                >
                  + Add a card
                </Button>
              </div>
              
              <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-muted/30">
                <CreditCard className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  No payment methods available -{" "}
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-primary"
                    onClick={() => setShowAddCard(true)}
                  >
                    Add Card
                  </Button>
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5" />
                <span>Payments are 256-bit SSL encrypted</span>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
